import { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";

export default function ColorModal({
  open,
  onClose,
  onSave,
  color = null,
}) {
  const [form, setForm] = useState({
    name: "",
    code: "",
    hexCode: "#000000",
    sortOrder: 0,
    status: "Active",
  });
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    setError("");
    setForm({
      name: color?.name || "",
      code: color?.code || "",
      hexCode: color?.hexCode || "#000000",
      sortOrder: color?.sortOrder ?? 0,
      status: color?.isActive === false ? "Inactive" : "Active",
    });
  }, [open, color]);

  if (!open) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.name.trim() || !form.code.trim()) {
      setError("Color name and code are required.");
      return;
    }

    if (!/^#[0-9A-Fa-f]{6}$/.test(form.hexCode)) {
      setError("Enter a valid six-digit hex color, for example #7A2238.");
      return;
    }

    try {
      setIsSaving(true);
      setError("");

      await onSave({
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        hexCode: form.hexCode.toUpperCase(),
        sortOrder: Number(form.sortOrder) || 0,
        isActive: form.status === "Active",
      });

      onClose();
    } catch (requestError) {
      const message = requestError?.details?.message || requestError.message;

      setError(
        Array.isArray(message)
          ? message.join(", ")
          : message || "Unable to save color.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="w-full max-w-xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-8 py-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {color ? "Edit Color" : "Add Color"}
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Manage colors used in products and variants.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
          >
            <FiX size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6 p-8">
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                {error}
              </div>
            )}

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Color Name *
              </label>

              <input
                type="text"
                placeholder="Maroon"
                value={form.name}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    name: event.target.value,
                  }))
                }
                className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Color Code *
              </label>

              <input
                type="text"
                placeholder="MAROON"
                value={form.code}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    code: event.target.value.toUpperCase(),
                  }))
                }
                className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
                required
              />
            </div>

            <div>
              <label className="mb-3 block text-sm font-medium text-gray-700">
                Color
              </label>

              <div className="flex items-center gap-4">
                <input
                  type="color"
                  value={form.hexCode}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      hexCode: event.target.value,
                    }))
                  }
                  className="h-14 w-16 cursor-pointer rounded-xl border border-gray-200"
                />

                <input
                  type="text"
                  value={form.hexCode}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      hexCode: event.target.value,
                    }))
                  }
                  placeholder="#7A2238"
                  className="h-12 flex-1 rounded-xl border border-gray-200 px-4 uppercase outline-none focus:border-primary-700"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Sort Order
              </label>

              <input
                type="number"
                min="0"
                value={form.sortOrder}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    sortOrder: event.target.value,
                  }))
                }
                className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
              />
            </div>

            <div className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-gray-50 p-4">
              <div
                className="h-14 w-14 rounded-2xl border border-gray-200"
                style={{ backgroundColor: form.hexCode }}
              />

              <div>
                <h4 className="font-semibold text-gray-900">
                  {form.name || "Color Name"}
                </h4>
                <p className="text-sm text-gray-500">{form.hexCode}</p>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Status
              </label>

              <select
                value={form.status}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    status: event.target.value,
                  }))
                }
                className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
              >
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-100 px-8 py-5">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="h-11 rounded-xl border border-gray-200 px-6 font-medium disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSaving}
              className="h-11 rounded-xl bg-primary-700 px-6 font-medium text-white hover:bg-primary-800 disabled:opacity-60"
            >
              {isSaving ? "Saving..." : "Save Color"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}